import { useEffect, useMemo, useState } from 'react'
import { api } from './api'
import { byQueue, byTicket, type QueueEstimate, type TicketEstimate } from './estimate'
import type { QueueView } from './types'

/** How long the floor must be still before the estimates are read again. */
const SETTLE_MS = 500
/** Read again this often anyway: a desk's pace moves with no delta at all. */
const REFRESH_MS = 30_000

export interface Estimates {
  /** What a new standard ticket would be told, per queue. */
  queues: ReadonlyMap<string, QueueEstimate>
  /** Where each waiting ticket stands. */
  tickets: ReadonlyMap<string, TicketEstimate>
}

/**
 * The server's estimates for a branch: read once the live floor settles after a
 * change, and every half minute. Until the first answer, the snapshot's own
 * per-queue estimate stands in; deltas do not carry it.
 */
export function useEstimates(branchId: string | undefined, floor: { queues: QueueView[] } | null | undefined): Estimates {
  const [got, setGot] = useState<{ branchId: string; queues: Map<string, QueueEstimate>; tickets: Map<string, TicketEstimate> } | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!branchId) return
    const ctl = new AbortController()
    const timer = setTimeout(() => {
      api.estimates(branchId, ctl.signal).then(
        (r) => {
          if (!ctl.signal.aborted) setGot({ branchId, queues: byQueue(r.queues), tickets: byTicket(r.tickets) })
        },
        () => undefined,
      )
    }, SETTLE_MS)
    return () => {
      clearTimeout(timer)
      ctl.abort()
    }
  }, [branchId, floor, tick])

  return useMemo(() => {
    if (got && got.branchId === branchId) return got
    const fromSnapshot = (floor?.queues ?? []).flatMap((q) => (q.newCustomer ? [q.newCustomer] : []))
    return { queues: byQueue(fromSnapshot), tickets: new Map() }
  }, [got, branchId, floor])
}
